import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { HelpBubble } from "../components/HelpBubble";
import {
  Search, FileText, Filter, ChevronRight,
  Loader2, Activity, Tag,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { contractStatusColors, formatCurrency } from "../components/management/constants";
import { ContractManagementPanel } from "../components/management/ContractManagementPanel";

export default function ContractManagementPage() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [panelExpanded, setPanelExpanded] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    contract_type: "", supplier_name: "", creator_name: "",
    owner_name: "", department: "", spend_category: "", status: "",
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchContracts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFilterOptions = async () => {
    try {
      const resp = await apiClient.get("/contract-management/filters");
      setFilterOptions(resp.data);
    } catch (e) { /* ignore */ }
  };

  const fetchContracts = async (overrideFilters, overrideSearch) => {
    setLoading(true);
    try {
      const f = overrideFilters || filters;
      const q = overrideSearch !== undefined ? overrideSearch : searchQuery;
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      Object.entries(f).forEach(([k, v]) => { if (v) params.append(k, v); });
      const resp = await apiClient.get(`/contract-management/search?${params.toString()}`);
      setContracts(resp.data);
    } catch (e) {
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchContracts();
  const handleClearFilters = () => {
    const cleared = { contract_type: "", supplier_name: "", creator_name: "", owner_name: "", department: "", spend_category: "", status: "" };
    setFilters(cleared);
    setSearchQuery("");
    fetchContracts(cleared, "");
  };
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contract-management-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Contract Management <HelpBubble tip="Post-execution lifecycle management — track deliverables, KPIs, milestones, obligations, notes, and generate management reports. Renewal and signing reminders notify you automatically before key dates." />
        </h1>
        <p className="text-slate-500 mt-1">Monitor deliverables, KPIs, performance, and relationship management</p>
      </div>

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <Input
              placeholder="Search contracts... (e.g., 'acme IT expiring')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="pl-10 rounded-sm"
              data-testid="management-search-input"
            />
          </div>
          <Button onClick={handleSearch} className="gap-2 rounded-sm" data-testid="management-search-btn"><Search size={16} />Search</Button>
          <Button variant="outline" onClick={() => setFiltersOpen(!filtersOpen)} className={`gap-2 rounded-sm ${activeFilterCount > 0 ? "border-cyan-500 text-cyan-600" : ""}`} data-testid="toggle-filters-btn">
            <Filter size={16} />
            Filters
            {activeFilterCount > 0 && <Badge className="ml-1 bg-cyan-500 text-white h-5 w-5 p-0 flex items-center justify-center text-xs">{activeFilterCount}</Badge>}
          </Button>
        </div>

        {filtersOpen && (
          <Card className="border-cyan-200/50">
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Contract Type</Label>
                  <Select value={filters.contract_type} onValueChange={(v) => setFilters({ ...filters, contract_type: v === "_all" ? "" : v })}>
                    <SelectTrigger className="rounded-sm h-9" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All types</SelectItem>
                      {(filterOptions.contract_types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Supplier Name</Label>
                  <Select value={filters.supplier_name} onValueChange={(v) => setFilters({ ...filters, supplier_name: v === "_all" ? "" : v })}>
                    <SelectTrigger className="rounded-sm h-9" data-testid="filter-supplier"><SelectValue placeholder="All suppliers" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All suppliers</SelectItem>
                      {(filterOptions.suppliers || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Creator / Owner</Label>
                  <Select value={filters.creator_name} onValueChange={(v) => setFilters({ ...filters, creator_name: v === "_all" ? "" : v })}>
                    <SelectTrigger className="rounded-sm h-9" data-testid="filter-creator"><SelectValue placeholder="All users" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All users</SelectItem>
                      {(filterOptions.users || []).map(u => <SelectItem key={u.id} value={u.name}>{u.name} ({u.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Department</Label>
                  <Select value={filters.department} onValueChange={(v) => setFilters({ ...filters, department: v === "_all" ? "" : v })}>
                    <SelectTrigger className="rounded-sm h-9" data-testid="filter-department"><SelectValue placeholder="All depts" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All departments</SelectItem>
                      {(filterOptions.departments || []).map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Spend Category</Label>
                  <Select value={filters.spend_category} onValueChange={(v) => setFilters({ ...filters, spend_category: v === "_all" ? "" : v })}>
                    <SelectTrigger className="rounded-sm h-9" data-testid="mgmt-filter-spend-category"><SelectValue placeholder="All categories" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All categories</SelectItem>
                      {(filterOptions.spend_categories || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "_all" ? "" : v })}>
                    <SelectTrigger className="rounded-sm h-9" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">All statuses</SelectItem>
                      {(filterOptions.statuses || []).map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={handleClearFilters} className="w-full rounded-sm" data-testid="clear-filters-btn">Clear All</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Results */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-6">
        {/* Contract List */}
        <div className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-400">{contracts.length} contract(s)</p>

          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
          ) : contracts.length > 0 ? (
            <ScrollArea className="h-[calc(100vh-320px)]">
              <div className="space-y-2 pr-2">
                {contracts.map((contract) => (
                  <div
                    key={contract.id}
                    className={`p-4 border rounded-sm cursor-pointer transition-all hover:shadow-sm ${selectedContractId === contract.id ? "border-cyan-500 bg-cyan-50/50 dark:bg-cyan-950/20" : "hover:border-slate-300"}`}
                    onClick={() => setSelectedContractId(contract.id)}
                    data-testid={`management-contract-${contract.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{contract.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge className={`rounded-sm text-xs ${contractStatusColors[contract.status]}`}>{contract.status}</Badge>
                          <span className="text-xs text-slate-500">{contract.contract_type}</span>
                          {contract.spend_category && <Badge variant="outline" className="text-xs gap-1"><Tag size={10} />{contract.spend_category}</Badge>}
                        </div>
                      </div>
                      <ChevronRight size={16} className={`text-slate-400 transition-transform ${selectedContractId === contract.id ? "rotate-90" : ""}`} />
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span>{contract.contracting_party?.legal_name || "-"}</span>
                      {contract.contract_value && <span className="font-mono">{formatCurrency(contract.contract_value, contract.currency)}</span>}
                      <span>by {contract.creator_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-16">
              <FileText size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No contracts found matching your criteria</p>
            </div>
          )}
        </div>

        {/* Management Panel */}
        {!panelExpanded && (
          <div className="lg:border-l lg:pl-6">
            {selectedContractId ? (
              <ContractManagementPanel
                key={selectedContractId}
                contractId={selectedContractId}
                onClose={() => setSelectedContractId(null)}
                expanded={false}
                onToggleExpand={() => setPanelExpanded(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <Activity size={48} className="text-slate-200 mb-4" />
                <p className="text-slate-500 font-medium">Select a contract to manage</p>
                <p className="text-sm text-slate-400 mt-1">Track performance, KPIs, changes, notes, and schedule reviews</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fullscreen Expanded Panel Overlay */}
      {panelExpanded && selectedContractId && createPortal(
        <div className="fixed inset-0 bg-white dark:bg-slate-950" style={{ zIndex: 9999 }} data-testid="expanded-panel-overlay">
          <div className="h-full overflow-y-auto p-6 lg:p-10 max-w-6xl mx-auto">
            <ContractManagementPanel
              key={`expanded-${selectedContractId}`}
              contractId={selectedContractId}
              onClose={() => setPanelExpanded(false)}
              expanded={true}
              onToggleExpand={() => setPanelExpanded(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
